import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import TicketForm from "./TicketForm";
import Chat from "./Discussion/Chat";
import "../../styling/TicketContainer.css";
import { useApiClient } from "../../service/API";
import { useAuth } from "../../auth/AuthHandler";
import { useWebSocketContext } from "../WebSocket/WebSocketContext";
import TicketStatusLog from "./TicketStatusLog";
import StageFlowPoints from "./StageFlowPoints";
import BORemark from "./BoRemarks";
import ErrorReworkList from "./ErrorReworkList";
import { useTicket } from "../../hooks/useTickets";
import { useUrgentRequests } from "../../hooks/useUrgentRequests";

function TicketContainer({ title }) {
  const { ticketId } = useParams();
  const location = useLocation();
  const apiClient = useApiClient();
  const apiRef = useRef(apiClient);
  useEffect(() => { apiRef.current = apiClient; }, [apiClient]);

  const { userEmail } = useAuth();
  const routeSeed = location.state?.ticketData;
  const seed = title !== "create-ticket" && ticketId != null &&
    routeSeed?.id != null && String(routeSeed.id) === String(ticketId)
      ? routeSeed
      : null;
  const { latestTicketEvents } = useWebSocketContext();

  const {
    urgentList,
    loadingUrgents,
    refreshUrgentList,
    pendingUrgent,
    activeUrgent,
    urgentLock,
  } = useUrgentRequests({
    ticketId,
    wsMessage: latestTicketEvents,
    enabled: title === "view-ticket",
  });

  // Unique key that changes on page mode, ticketId, or ?key= param
  const createKey = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("key") ?? "__no_key__";
  }, [location.search]);
  const remountKey = useMemo(
    () => `${title}-${ticketId ?? "create"}-${createKey}`,
    [title, ticketId, createKey]
  );

  // Single source of truth for main ticket
  const {
    data: loadedTicket,
    loading,
    error,
    refetch,
    applyIfNewer,
  } = useTicket({ ticketId, seed, wsMessage: latestTicketEvents });

  // A new route must never render a form using another ticket's snapshot.
  const existingTicket = title !== "create-ticket" && ticketId != null &&
    loadedTicket?.id != null && String(loadedTicket.id) === String(ticketId)
      ? loadedTicket
      : null;

  // Side panel states
  const [receiverEmail, setReceiverEmail] = useState(null);
  const [isChatDisabled, setIsChatDisabled] = useState(false);
  const [ticketStatusLog, setTicketStatusLog] = useState([]);
  const [errorReworkList, setErrorReworkList] = useState([]);

  // Reset side panels immediately when the route context changes
  useEffect(() => {
    setTicketStatusLog([]);
    setErrorReworkList([]);
    setReceiverEmail(null);
    setIsChatDisabled(false);
    lastPanelsForRef.current = { id: null, updatedAt: null };
  }, [ticketId, title]);

  // Compute chat state
  useEffect(() => {
    if (!existingTicket) return;
    const done = ["DONE", "CANCELLED"].includes(existingTicket.status);
    setIsChatDisabled(done);
    setReceiverEmail(
      existingTicket.assignedTo === userEmail
        ? existingTicket.createdBy
        : existingTicket.assignedTo
    );
  }, [existingTicket, userEmail]);

  // --- Side panels fetcher (deduped via a small memory) ---
  const lastPanelsForRef = useRef({ id: null, updatedAt: null });

  const refetchSidePanels = useCallback(
    async (opts = { force: false }) => {
      if (!ticketId || title !== "view-ticket") return;

      const curUpdatedAt = existingTicket?.updatedAt ?? null;
      const curId = ticketId ?? null;

      if (!opts.force) {
        const last = lastPanelsForRef.current;
        if (last.id === curId && last.updatedAt === curUpdatedAt) {
          // Nothing new; skip
          return;
        }
      }

      try {
        const [logRes, erRes] = await Promise.all([
          apiRef.current.get(`/tickets/status-log/${curId}`),
          apiRef.current.get(`/tickets/error-rework/${curId}`),
        ]);
        setTicketStatusLog(logRes.data);
        setErrorReworkList(erRes.data);
        lastPanelsForRef.current = { id: curId, updatedAt: curUpdatedAt };
      } catch {
        // ignore panel errors for UX
      }
    },
    [ticketId, title, existingTicket?.updatedAt]
  );

  // 1) Fetch panels when ticket changes or we load a different page mode
  useEffect(() => {
    if (!ticketId || title !== "view-ticket") return;
    refetchSidePanels({ force: true }); // ensure a fresh load on route change
  }, [ticketId, title, refetchSidePanels]);

  // 2) If stage or status changes (locally or from server), force a refresh
  const prevStageStatus = useRef({ stage: null, status: null });
  useEffect(() => {
    if (!existingTicket) return;
    const { stage, status } = existingTicket;
    const prev = prevStageStatus.current;
    if (prev.stage !== stage || prev.status !== status) {
      prevStageStatus.current = { stage, status };
      refetchSidePanels({ force: true });
    }
  }, [existingTicket, refetchSidePanels]);

  // Block only on the very first load (no ticket data yet)
  const initialBlocking = (title !== "create-ticket") && loading && !existingTicket;
  if (initialBlocking) return <div>Loading...</div>;

  if (title !== "create-ticket" && error && !existingTicket) {
    return (
      <div role="alert">
        Unable to load this ticket.
        <button type="button" onClick={() => refetch(ticketId)}>Retry</button>
      </div>
    );
  }

  if ((title === "view-ticket" || title === "update-ticket") && !existingTicket) {
    return <div>Ticket not found.</div>;
  }

  return (
    <div>
      {title !== "create-ticket" && existingTicket?.serviceType && (
        <StageFlowPoints ticket={existingTicket} />
      )}

      <div className="ticket-container">
        <div
          className={`ticket-form-container ${
            title !== "view-ticket" ? "dont-show-chat" : "show-chat"
          }`}
        >
          <TicketForm
            key={remountKey}
            title={title}
            ticketInfo={existingTicket || null}
            ticketId={ticketId || existingTicket?.id || null}

            urgentList={urgentList}
            loadingUrgents={loadingUrgents}
            pendingUrgent={pendingUrgent}
            activeUrgent={activeUrgent}
            urgentLock={urgentLock}
            onRefreshUrgents={() => refreshUrgentList({ force: true })}

            onTicketPatched={(serverData) => {
              applyIfNewer(serverData);
              refetchSidePanels({ force: true });
              refreshUrgentList({ force: true }); // ✅ keep urgents synced on local patch too
            }}
            onSidePanelsInvalidated={() => {
              refetchSidePanels({ force: true });
            }}
          />
        </div>

        {title === "view-ticket" && ticketId && (
          <div className="ticket-discussion-container">
            <Chat
              key={`chat-${ticketId}`}
              senderEmail={userEmail}
              ticketId={ticketId}
              receiverEmail={receiverEmail}
              isChatDisabled={isChatDisabled}
            />
            <ErrorReworkList data={errorReworkList} />
            <BORemark ticket={existingTicket} />
            <TicketStatusLog ticketstatuslog={ticketStatusLog} />
          </div>
        )}

      </div>
    </div>
  );
}

export default TicketContainer;
